import { createEffect, createSignal, type JSX, on, Show, splitProps } from 'solid-js'
import { Button } from './button.tsx'
import { Dialog } from './dialog.tsx'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** State the consequence, not the question. "Вигляд буде видалено для всієї команди." */
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Renders the confirm action as destructive. Defaults to true — this is a confirmation. */
  destructive?: boolean
  /** Override the default irreversible-action note, or pass `null` when no extra note is needed. */
  notice?: string | null
  pending?: boolean
  confirmDisabled?: boolean
  children?: JSX.Element
  onConfirm: () => void
  onClose: () => void
}

/**
 * The in-app replacement for `window.confirm`.
 *
 * `window.confirm` looks like a browser error, cannot be styled or translated consistently, and —
 * the reason it was removed here — Chrome suppresses it outright once a page has shown a few,
 * after which the action it guards silently stops working with no error anywhere.
 */
export const ConfirmDialog = (props: ConfirmDialogProps) => {
  const [local] = splitProps(props, [
    'open',
    'title',
    'description',
    'confirmLabel',
    'cancelLabel',
    'destructive',
    'notice',
    'pending',
    'confirmDisabled',
    'children',
    'onConfirm',
    'onClose',
  ])
  const [saving, setSaving] = createSignal(false)
  const [error, setError] = createSignal('')
  const pending = () => local.pending || saving()
  createEffect(
    on(
      () => local.open,
      () => setError(''),
    ),
  )
  const close = () => {
    if (!pending()) local.onClose()
  }
  const confirm = async () => {
    if (pending() || local.confirmDisabled) return
    setSaving(true)
    setError('')
    try {
      await local.onConfirm()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не вдалося зберегти зміни')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={local.open}
      title={local.title}
      description={local.description}
      size="sm"
      onClose={close}
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            disabled={pending()}
            onPointerDown={(event) => event.preventDefault()}
            onClick={close}
          >
            {local.cancelLabel ?? 'Скасувати'}
          </Button>
          <Button
            variant={local.destructive === false ? 'primary' : 'danger'}
            size="sm"
            disabled={pending() || local.confirmDisabled}
            onPointerDown={(event) => event.preventDefault()}
            onClick={confirm}
          >
            {local.confirmLabel ?? 'Видалити'}
          </Button>
        </>
      }
    >
      {local.children}
      <Show when={local.notice !== null}>
        <p class="text-body text-text-muted">{local.notice ?? 'Цю дію не можна скасувати.'}</p>
      </Show>
      <Show when={error()}>
        <p role="alert" class="mt-3 text-body text-status-critical">
          {error()}
        </p>
      </Show>
    </Dialog>
  )
}
